// The four matrix operations a single orbit camera needs, and nothing else.
//
// Pulling in gl-matrix (or three.js, at ~600 kB) to multiply some 4x4s would
// more than double this app's bundle for one view. Column-major, matching what
// WebGL's uniformMatrix4fv expects with transpose=false.

export type Mat4 = Float32Array;

export function identity(): Mat4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

export function perspective(fovyRad: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan(fovyRad / 2);
  const nf = 1 / (near - far);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) * nf;
  m[11] = -1;
  m[14] = 2 * far * near * nf;
  return m;
}

export function multiply(a: Mat4, b: Mat4): Mat4 {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      o[c * 4 + r] =
        a[r] * b[c * 4] +
        a[4 + r] * b[c * 4 + 1] +
        a[8 + r] * b[c * 4 + 2] +
        a[12 + r] * b[c * 4 + 3];
    }
  }
  return o;
}

/**
 * View matrix for a camera orbiting the origin.
 *
 * `yaw` spins around the vertical axis, `pitch` lifts over the pole (clamped by
 * the caller to avoid gimbal flip at the poles), `dist` is the orbit radius.
 */
export function orbitView(yaw: number, pitch: number, dist: number): Mat4 {
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const cp = Math.cos(pitch), sp = Math.sin(pitch);

  // Camera position on the sphere.
  const ex = dist * cp * sy;
  const ey = dist * sp;
  const ez = dist * cp * cy;

  // Look at the origin: forward = -eye normalised.
  const fx = -ex / dist, fy = -ey / dist, fz = -ez / dist;

  // right = normalize(cross(forward, worldUp)), worldUp = +Y
  let rx = fz, ry = 0, rz = -fx;
  const rl = Math.hypot(rx, ry, rz) || 1;
  rx /= rl; ry /= rl; rz /= rl;

  // up = cross(right, forward)
  const ux = ry * fz - rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy - ry * fx;

  const m = new Float32Array(16);
  m[0] = rx; m[4] = ry; m[8] = rz; m[12] = -(rx * ex + ry * ey + rz * ez);
  m[1] = ux; m[5] = uy; m[9] = uz; m[13] = -(ux * ex + uy * ey + uz * ez);
  m[2] = -fx; m[6] = -fy; m[10] = -fz; m[14] = fx * ex + fy * ey + fz * ez;
  m[3] = 0; m[7] = 0; m[11] = 0; m[15] = 1;
  return m;
}
